import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Stack,
  Alert,
} from '@mui/material';
import { IoClose } from 'react-icons/io5';

interface CreateClassModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (title: string) => Promise<void>;
  isSubmitting?: boolean;
}

export const CreateClassModal: React.FC<CreateClassModalProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Grade / Class title is required (e.g., Grade 6, Grade 7, Grade 8).');
      return;
    }

    try {
      setError(null);
      await onSubmit(title.trim());
      setTitle('');
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create Grade Level.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#1A202C">
            Add Grade Level / Course
          </Typography>

          <Typography variant="caption" color="text.secondary">
            Create a top-level grade container (e.g., Grade 6, Grade 7)
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <IoClose />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ py: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Stack spacing={2}>
            <TextField
              label="Grade / Class Title"
              placeholder="e.g. Grade 6, Grade 7, Grade 8, HS Algebra"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              size="small"
              required
              autoFocus
              helperText="Cohorts (like 7A, 7B) will be created inside this Grade level."
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#EDB42E',
              color: '#000',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#d9a224' },
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Grade'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
